import type { CardInstanceId, GameState, LaneId, PlayerId, PlayerLaneState } from "../types";
import { getOpponentPlayerId } from "../types";

/**
 * Temporary interpretation tracked as FK-RULE-001: lane defeat Exhausts the
 * losing Conspiracy and Commits the winning Conspiracy with the rest of its
 * lane. Keep this policy isolated here until the owner resolves the question.
 */
export const LANE_DEFEAT_CONSPIRACY_DESTINATION = "exhaust-loser-and-commit-winner" as const;

export interface LaneDefeatResolution {
  readonly state: GameState;
  readonly exhaustedCardInstanceIds: readonly CardInstanceId[];
  readonly victoryBanners: number;
  readonly matchFinished: boolean;
}

function unique(values: readonly CardInstanceId[]): readonly CardInstanceId[] {
  return [...new Set(values)];
}

function getCardsToExhaust(side: PlayerLaneState): readonly CardInstanceId[] {
  return unique([
    ...(side.land?.cardInstanceIds ?? []),
    ...(side.commander === null ? [] : [side.commander.cardInstanceId]),
    ...side.armies.map((army) => army.cardInstanceId),
    ...(side.conspiracyCardInstanceId === null ? [] : [side.conspiracyCardInstanceId]),
    ...side.attachedCardInstanceIds,
  ]);
}

export function resolveLaneDefeat(
  state: Readonly<GameState>,
  laneId: LaneId,
  winnerPlayerId: PlayerId,
): LaneDefeatResolution {
  const defeatedPlayerId = getOpponentPlayerId(winnerPlayerId);
  const lane = state.lanes[laneId];
  const winningSide = lane.players[winnerPlayerId];
  const defeatedSide = lane.players[defeatedPlayerId];

  if (
    lane.result !== "active" ||
    winningSide.status !== "active" ||
    winningSide.land === null ||
    defeatedSide.status !== "active" ||
    defeatedSide.land === null ||
    defeatedSide.land.currentControl !== 0
  ) {
    throw new Error("Lane defeat resolution requires an active lane with a Land at zero Control.");
  }

  const exhaustedCardInstanceIds = getCardsToExhaust(defeatedSide);
  const victoryBanners = state.players[winnerPlayerId].victoryBanners + 1;
  const matchFinished = victoryBanners >= 2;
  const committedWinningSide: PlayerLaneState = {
    ...winningSide,
    status: "committed",
  };
  const clearedDefeatedSide: PlayerLaneState = {
    status: "defeated",
    land: null,
    commander: null,
    armies: [],
    conspiracyCardInstanceId: null,
    attachedCardInstanceIds: [],
    combatModifiers: [],
  };

  return {
    state: {
      ...state,
      phase: matchFinished ? "finished" : state.phase,
      status: matchFinished
        ? {
            type: "finished",
            winnerPlayerId,
            reason: "two-victory-banners",
          }
        : state.status,
      players: {
        ...state.players,
        [winnerPlayerId]: {
          ...state.players[winnerPlayerId],
          victoryBanners,
        },
        [defeatedPlayerId]: {
          ...state.players[defeatedPlayerId],
          exhaust: unique([
            ...state.players[defeatedPlayerId].exhaust,
            ...exhaustedCardInstanceIds,
          ]),
        },
      },
      lanes: {
        ...state.lanes,
        [laneId]: {
          ...lane,
          result: winnerPlayerId === "player-1" ? "player-1-won" : "player-2-won",
          players: {
            ...lane.players,
            [winnerPlayerId]: committedWinningSide,
            [defeatedPlayerId]: clearedDefeatedSide,
          },
        },
      },
    },
    exhaustedCardInstanceIds,
    victoryBanners,
    matchFinished,
  };
}
